import React, { useEffect, createContext, useContext } from "react";
import { useDispatch } from "react-redux";
import { fetchPatient } from "../../store/slices/patientsSlice";
import Swal from "sweetalert2";

// Public storage base for previewing already-saved signature images
const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
export const resolveSignatureSrc = (v) => {
  if (!v) return null;
  return v.startsWith("data:") ? v : `${STORAGE_URL}${v}`;
};

// Context lets hoisted helper components read form state without being
// redefined on every render (which made inputs lose focus each keystroke).
const Irp2FormCtx = createContext(null);

const Check = ({ name, label }) => {
  const { formData, setFormData, readOnly } = useContext(Irp2FormCtx);
  return (
    <div className="col-md-4 col-sm-6">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id={`chk_${name}`}
          checked={!!formData[name]}
          onChange={() => !readOnly && setFormData((prev) => ({ ...prev, [name]: !prev[name] }))}
          disabled={readOnly}
        />
        <label className="form-check-label" htmlFor={`chk_${name}`}>{label}</label>
      </div>
    </div>
  );
};

const SignRow = ({ role, sigField, dateField, roleText, extraField, extraLabel }) => {
  const { formData, setFormData, readOnly } = useContext(Irp2FormCtx);
  const src = resolveSignatureSrc(formData[sigField]);

  const onUpload = (file) => {
    if (readOnly || !file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire({ title: "Invalid file", text: "Please upload an image file.", icon: "warning" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFormData((prev) => ({ ...prev, [sigField]: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="row g-3 align-items-center border-bottom py-3">
      <div className="col-md-3 fw-semibold">{role}</div>
      <div className="col-md-4">
        {src ? (
          <div className="d-flex align-items-center gap-2">
            <img src={src} alt={`${role} signature`} style={{ height: 55, maxWidth: 170, objectFit: "contain", border: "1px solid #dee2e6", borderRadius: 4, background: "#fff" }} />
            {!readOnly && (
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setFormData((prev) => ({ ...prev, [sigField]: "" }))}>
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        ) : (
          <input type="file" accept="image/*" className="form-control form-control-sm" onChange={(e) => onUpload(e.target.files[0])} disabled={readOnly} />
        )}
      </div>
      <div className="col-md-2">
        <div className="form-floating form-floating-outline">
          <input type="date" className="form-control" value={formData[dateField] || ""} onChange={(e) => setFormData((prev) => ({ ...prev, [dateField]: e.target.value }))} disabled={readOnly} />
          <label>Date</label>
        </div>
      </div>
      <div className="col-md-3">
        {extraField ? (
          <div className="form-floating form-floating-outline">
            <input type="text" className="form-control" value={formData[extraField] || ""} onChange={(e) => setFormData((prev) => ({ ...prev, [extraField]: e.target.value }))} disabled={readOnly} />
            <label>{extraLabel}</label>
          </div>
        ) : (
          <span className="text-muted small">{roleText}</span>
        )}
      </div>
    </div>
  );
};

/**
 * Individual Rehabilitation Plan Form (2)
 * CARF-aligned. Layout mirrors IndividualClientFileForm.js (Bootstrap accordions).
 * Repeater arrays on formData use snake_case keys that match the API relation
 * serialization: recovery_capitals, strengths_growths, objectives, self_reviews.
 */
const IndividualRehabPlanTwoForm = ({ formData, setFormData, readOnly = false }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (formData.patient_id && !formData.client_name) {
      dispatch(fetchPatient(formData.patient_id))
        .unwrap()
        .then((patient) => {
          setFormData((prev) => ({
            ...prev,
            client_name: `${patient.first_name} ${patient.last_name}`,
            date_of_birth: patient.dob ? patient.dob.substring(0, 10) : "",
          }));
        })
        .catch((err) => console.error("Failed to prefill patient details:", err));
    }
  }, [dispatch, formData.patient_id, formData.client_name, setFormData]);

  // ---------------- Scalar helpers ----------------
  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------- Repeater helpers ----------------
  const addRow = (key, blank) => {
    if (readOnly) return;
    setFormData((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), { id: Date.now() + Math.random(), ...blank }],
    }));
  };

  const removeRow = (key, id) => {
    if (readOnly) return;
    const list = formData[key] || [];
    if (list.length <= 1) {
      Swal.fire({ title: "Cannot Delete", text: "At least one entry must remain.", icon: "warning" });
      return;
    }
    Swal.fire({
      title: "Remove this entry?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then((res) => {
      if (res.isConfirmed) {
        setFormData((prev) => ({ ...prev, [key]: prev[key].filter((r) => r.id !== id) }));
      }
    });
  };

  const changeRow = (key, id, field, value) => {
    if (readOnly) return;
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    }));
  };

  const recoveryCapitals = formData.recovery_capitals || [];
  const strengthsGrowths = formData.strengths_growths || [];
  const objectives = formData.objectives || [];
  const selfReviews = formData.self_reviews || [];
  const smartGoals = formData.smart_goals || [];

  return (
    <Irp2FormCtx.Provider value={{ formData, setFormData, readOnly }}>
    <div className="p-3" style={{ maxHeight: "70vh", overflowY: "auto", scrollbarWidth: "thin" }}>
      <div className="accordion" id="irp2Accordion">
        {/* ===== Accordion 1 — Client Identification & Plan Details ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section1">
              1. Client Identification &amp; Plan Details
            </button>
          </h2>
          <div id="irp2Section1" className="accordion-collapse collapse show">
            <div className="accordion-body pt-3">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="client_name" value={formData.client_name || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Client Name</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_plan" value={formData.date_of_plan || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Plan</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="date_of_birth" value={formData.date_of_birth || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Date of Birth</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="plan_period" value={formData.plan_period || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Plan Period</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="diagnosis" value={formData.diagnosis || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Diagnosis (if applicable)</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="rehab_goal_no" value={formData.rehab_goal_no || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Rehab Goal #</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="prp_practitioner" value={formData.prp_practitioner || ""} onChange={handleChange} disabled={readOnly} />
                    <label>PRP Practitioner</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="program_site" value={formData.program_site || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Program / Site</label>
                  </div>
                </div>
              </div>
              <p className="text-muted small mt-3 mb-0">
                <em>CARF Standard: All identifying information is treated as confidential and shared only with informed client consent.</em>
              </p>
            </div>
          </div>
        </div>

        {/* ===== Accordion 2 — Environment of Choice ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section2">
              2. Environment of Choice (Domain of Focus)
            </button>
          </h2>
          <div id="irp2Section2" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-2">Select the primary environment the client wishes to focus on (check one):</p>
              <div className="row g-2 mb-3">
                <Check name="env_living" label="Living" />
                <Check name="env_learning" label="Learning" />
                <Check name="env_working" label="Working" />
                <Check name="env_socializing" label="Socializing" />
              </div>
              <div className="form-floating form-floating-outline">
                <textarea className="form-control" style={{ height: "100px" }} name="preferred_environment_details" value={formData.preferred_environment_details || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Preferred Environment Details</label>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Accordion 3 — Collaborative Development & Informed Consent ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section3">
              3. Collaborative Development &amp; Informed Consent
            </button>
          </h2>
          <div id="irp2Section3" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-2">This plan was developed collaboratively with (select all that apply):</p>
              <div className="row g-2 mb-3">
                <Check name="collab_client_self" label="Client (Self)" />
                <Check name="collab_prp" label="PRP Practitioner" />
                <Check name="collab_family_caregiver" label="Family / Caregiver" />
                <Check name="collab_interdisciplinary" label="Interdisciplinary Team" />
                <Check name="collab_other" label="Other" />
              </div>
              <div className="form-floating form-floating-outline mb-3">
                <textarea className="form-control" style={{ height: "100px" }} name="clients_voice" value={formData.clients_voice || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Client's Voice &amp; Preferences</label>
              </div>
              <div className="alert alert-light border small mb-0">
                <strong>Client Consent Statement:</strong> I, the undersigned, confirm that I have been actively involved in
                developing this Individual Rehabilitation Plan. The goals reflect my personal recovery aspirations and have
                been explained to me in language I understand. I consent to the use of this plan to guide my rehabilitation
                and agree to review progress as outlined below. I understand I may withdraw consent or request revisions at any time.
              </div>
            </div>
          </div>
        </div>

        {/* ===== Accordion 4 — Recovery Capital Assessment (repeater) ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section4">
              4. Recovery Capital Assessment
            </button>
          </h2>
          <div id="irp2Section4" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">
                Recovery capital refers to the breadth and depth of internal and external resources that can support the client's recovery journey.
              </p>
              {recoveryCapitals.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Entry {idx + 1}</h6>
                    <div>
                      {!readOnly && recoveryCapitals.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("recovery_capitals", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.personal_capital || ""} onChange={(e) => changeRow("recovery_capitals", row.id, "personal_capital", e.target.value)} disabled={readOnly}></textarea>
                        <label>Personal Capital</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.social_family || ""} onChange={(e) => changeRow("recovery_capitals", row.id, "social_family", e.target.value)} disabled={readOnly}></textarea>
                        <label>Social / Family</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.community || ""} onChange={(e) => changeRow("recovery_capitals", row.id, "community", e.target.value)} disabled={readOnly}></textarea>
                        <label>Community</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.financial_housing || ""} onChange={(e) => changeRow("recovery_capitals", row.id, "financial_housing", e.target.value)} disabled={readOnly}></textarea>
                        <label>Financial / Housing</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("recovery_capitals", { personal_capital: "", social_family: "", community: "", financial_housing: "" })}>
                  <i className="ri-add-line me-1"></i> Add Row
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== Accordion 5 — Strengths & Areas for Growth (repeater) ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section5">
              5. Strengths &amp; Areas for Growth (PROs)
            </button>
          </h2>
          <div id="irp2Section5" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              {strengthsGrowths.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Entry {idx + 1}</h6>
                    <div>
                      {!readOnly && strengthsGrowths.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("strengths_growths", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.strengths || ""} onChange={(e) => changeRow("strengths_growths", row.id, "strengths", e.target.value)} disabled={readOnly}></textarea>
                        <label>Strengths (Skills &amp; Resources Possessed)</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.areas_for_growth || ""} onChange={(e) => changeRow("strengths_growths", row.id, "areas_for_growth", e.target.value)} disabled={readOnly}></textarea>
                        <label>Areas for Growth (Skills &amp; Resources Needed)</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("strengths_growths", { strengths: "", areas_for_growth: "" })}>
                  <i className="ri-add-line me-1"></i> Add Row
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== Accordion 6 — Client-Centred SMART Goal Development ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section6">
              6. Client-Centred SMART Goal Development
            </button>
          </h2>
          <div id="irp2Section6" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">
                Add one entry per SMART goal. Each goal captures the client's input, PRP notes, and its start/end dates.
              </p>
              {smartGoals.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Goal {idx + 1}</h6>
                    <div>
                      {!readOnly && smartGoals.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("smart_goals", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3 mb-3">
                    <div className="col-md-12">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "80px" }} value={row.rehab_goal_statement || ""} onChange={(e) => changeRow("smart_goals", row.id, "rehab_goal_statement", e.target.value)} disabled={readOnly}></textarea>
                        <label>Rehabilitation Goal Statement</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.start_date || ""} onChange={(e) => changeRow("smart_goals", row.id, "start_date", e.target.value)} disabled={readOnly} />
                        <label>Start Date</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.end_date || ""} onChange={(e) => changeRow("smart_goals", row.id, "end_date", e.target.value)} disabled={readOnly} />
                        <label>End Date</label>
                      </div>
                    </div>
                  </div>
                  <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">SMART Criteria Breakdown</small>
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "10%" }}>SMART</th>
                          <th style={{ width: "30%" }}>Goal Component</th>
                          <th>Client Input</th>
                          <th>PRP Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["S – Specific", "What exactly will be achieved? (Who, What, Where, When, Why)", "smart_specific_client", "smart_specific_prp"],
                          ["M – Measurable", "How will progress be measured? (Frequency, amount, milestones)", "smart_measurable_client", "smart_measurable_prp"],
                          ["A – Achievable", "Is the goal realistic given strengths and resources?", "smart_achievable_client", "smart_achievable_prp"],
                          ["R – Relevant", "Is the goal meaningful to the client's recovery journey?", "smart_relevant_client", "smart_relevant_prp"],
                          ["T – Time-bound", "What is the target completion date?", "smart_timebound_client", "smart_timebound_prp"],
                        ].map(([label, comp, cField, pField]) => (
                          <tr key={cField}>
                            <td className="fw-semibold">{label}</td>
                            <td className="small">{comp}</td>
                            <td><textarea className="form-control form-control-sm" rows="2" value={row[cField] || ""} onChange={(e) => changeRow("smart_goals", row.id, cField, e.target.value)} disabled={readOnly}></textarea></td>
                            <td><textarea className="form-control form-control-sm" rows="2" value={row[pField] || ""} onChange={(e) => changeRow("smart_goals", row.id, pField, e.target.value)} disabled={readOnly}></textarea></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("smart_goals", { rehab_goal_statement: "", start_date: "", end_date: "", smart_specific_client: "", smart_specific_prp: "", smart_measurable_client: "", smart_measurable_prp: "", smart_achievable_client: "", smart_achievable_prp: "", smart_relevant_client: "", smart_relevant_prp: "", smart_timebound_client: "", smart_timebound_prp: "" })}>
                  <i className="ri-add-line me-1"></i> Add Goal
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== Accordion 7 — Objectives, Action Steps & Environment (repeater) ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section7">
              7. Objectives, Action Steps &amp; Environment
            </button>
          </h2>
          <div id="irp2Section7" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">Status codes: ✔ Achieved &nbsp; ⟳ In Progress &nbsp; ✕ Discontinued &nbsp; ↻ Modified</p>
              {objectives.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Objective {idx + 1}</h6>
                    <div>
                      {!readOnly && objectives.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("objectives", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.objective || ""} onChange={(e) => changeRow("objectives", row.id, "objective", e.target.value)} disabled={readOnly}></textarea>
                        <label>Objective</label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.action_steps || ""} onChange={(e) => changeRow("objectives", row.id, "action_steps", e.target.value)} disabled={readOnly}></textarea>
                        <label>Action Steps</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.environment_of_choice || ""} onChange={(e) => changeRow("objectives", row.id, "environment_of_choice", e.target.value)} disabled={readOnly} />
                        <label>Environment of Choice</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="text" className="form-control" value={row.frequency_duration || ""} onChange={(e) => changeRow("objectives", row.id, "frequency_duration", e.target.value)} disabled={readOnly} />
                        <label>Frequency &amp; Duration</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.target_date || ""} onChange={(e) => changeRow("objectives", row.id, "target_date", e.target.value)} disabled={readOnly} />
                        <label>Target Date</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <select className="form-select" value={row.obj_status || ""} onChange={(e) => changeRow("objectives", row.id, "obj_status", e.target.value)} disabled={readOnly}>
                          <option value="">Select</option>
                          <option value="Achieved">Achieved</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Discontinued">Discontinued</option>
                          <option value="Modified">Modified</option>
                        </select>
                        <label>Status</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("objectives", { objective: "", action_steps: "", environment_of_choice: "", frequency_duration: "", target_date: "", obj_status: "" })}>
                  <i className="ri-add-line me-1"></i> Add Objective
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== Accordion 8 — Progress Review Schedule ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section8">
              8. Progress Review Schedule
            </button>
          </h2>
          <div id="irp2Section8" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-2">Reviews are conducted collaboratively with the client at each interval below.</p>
              <div className="table-responsive">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "14%" }}>Review Period</th>
                      <th>Focus / Activity</th>
                      <th style={{ width: "16%" }}>Target Date</th>
                      <th style={{ width: "28%" }}>Outcome / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Twice Weekly", "Progress check-in with PRP practitioner", "review_twice_weekly_date", "review_twice_weekly_notes"],
                      ["Weekly", "Full goal review — SMART objectives assessed", "review_weekly_date", "review_weekly_notes"],
                      ["Monthly", "Formal progress evaluation — barriers & supports", "review_monthly_date", "review_monthly_notes"],
                      ["Quarterly", "Comprehensive plan review — revise if needed", "review_quarterly_date", "review_quarterly_notes"],
                      ["6-Monthly", "Outcome decision — Continue / Achieve / Modify / Discontinue", "review_6monthly_date", "review_6monthly_notes"],
                    ].map(([period, focus, dField, nField]) => (
                      <tr key={dField}>
                        <td className="fw-semibold">{period}</td>
                        <td className="small">{focus}</td>
                        <td><input type="date" className="form-control form-control-sm" name={dField} value={formData[dField] || ""} onChange={handleChange} disabled={readOnly} /></td>
                        <td><input type="text" className="form-control form-control-sm" name={nField} value={formData[nField] || ""} onChange={handleChange} disabled={readOnly} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Accordion 9 — Client Progress Self-Review Log (repeater) ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section9">
              9. Client Progress Self-Review Log
            </button>
          </h2>
          <div id="irp2Section9" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">
                The client is encouraged to document their own reflections at each review. This section belongs to the client.
              </p>
              {selfReviews.map((row, idx) => (
                <div key={row.id} className="border rounded-4 p-3 mb-3 bg-light-subtle">
                  <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                    <h6 className="mb-0 text-primary fw-bold">Log {idx + 1}</h6>
                    <div>
                      {!readOnly && selfReviews.length > 1 && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRow("self_reviews", row.id)}>
                          <i className="ri-delete-bin-line me-1"></i> Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <input type="date" className="form-control" value={row.review_date || ""} onChange={(e) => changeRow("self_reviews", row.id, "review_date", e.target.value)} disabled={readOnly} />
                        <label>Date</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.client_self_assessment || ""} onChange={(e) => changeRow("self_reviews", row.id, "client_self_assessment", e.target.value)} disabled={readOnly}></textarea>
                        <label>Client Self-Assessment</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.prp_observation || ""} onChange={(e) => changeRow("self_reviews", row.id, "prp_observation", e.target.value)} disabled={readOnly}></textarea>
                        <label>PRP Observation</label>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-floating form-floating-outline">
                        <textarea className="form-control" style={{ height: "70px" }} value={row.next_steps || ""} onChange={(e) => changeRow("self_reviews", row.id, "next_steps", e.target.value)} disabled={readOnly}></textarea>
                        <label>Next Steps</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {!readOnly && (
                <button type="button" className="btn btn-outline-primary" onClick={() => addRow("self_reviews", { review_date: "", client_self_assessment: "", prp_observation: "", next_steps: "" })}>
                  <i className="ri-add-line me-1"></i> Add Log Entry
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ===== Accordion 10 — Stage-Based Rehabilitation Model ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section10">
              10. Stage-Based Rehabilitation Model
            </button>
          </h2>
          <div id="irp2Section10" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">Minimum 60% Goal Achievement required for stage advancement.</p>
              <div className="table-responsive mb-3">
                <table className="table table-bordered align-middle">
                  <thead className="table-light">
                    <tr><th>Stage</th><th>Role</th><th>Activities</th><th style={{ width: "18%" }}>Date Achieved</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Stage 1</td><td>Client (Skill Building)</td><td>Structured program participation, core life skills</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage1_date_achieved" value={formData.stage1_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Stage 2</td><td>Observer</td><td>Observes sessions, builds insight, reduces stigma</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage2_date_achieved" value={formData.stage2_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Stage 3</td><td>Co-Facilitator</td><td>Assists sessions, applies skills</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage3_date_achieved" value={formData.stage3_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                    <tr>
                      <td>Stage 4</td><td>Facilitator</td><td>Leads sessions independently under PRP supervision</td>
                      <td><input type="date" className="form-control form-control-sm" name="stage4_date_achieved" value={formData.stage4_date_achieved || ""} onChange={handleChange} disabled={readOnly} /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-4">
                <div className="form-floating form-floating-outline">
                  <input type="text" className="form-control" name="duration_in_program" value={formData.duration_in_program || ""} onChange={handleChange} disabled={readOnly} />
                  <label>Duration in Program</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Accordion 11 — 6-Month Outcome Decision ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section11">
              11. 6-Month Outcome Decision
            </button>
          </h2>
          <div id="irp2Section11" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="outcome_review_date" value={formData.outcome_review_date || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Review Date</label>
                  </div>
                </div>
              </div>
              <label className="form-label fw-semibold small">Goal Status Decision:</label>
              <div className="row g-2 mb-3">
                <Check name="goal_status_continue" label="Continue" />
                <Check name="goal_status_achieved" label="Achieved" />
                <Check name="goal_status_modify" label="Modify" />
                <Check name="goal_status_discontinue" label="Discontinue" />
              </div>
              <div className="form-floating form-floating-outline mb-3">
                <textarea className="form-control" style={{ height: "80px" }} name="outcome_rationale" value={formData.outcome_rationale || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>Rationale</label>
              </div>
              <div className="form-floating form-floating-outline">
                <textarea className="form-control" style={{ height: "80px" }} name="new_goal" value={formData.new_goal || ""} onChange={handleChange} disabled={readOnly}></textarea>
                <label>New Goal (if applicable)</label>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Accordion 12 — Signatures & Authorization ===== */}
        <div className="accordion-item mb-3 shadow-sm rounded">
          <h2 className="accordion-header">
            <button className="accordion-button fw-semibold collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#irp2Section12">
              12. Signatures &amp; Authorization
            </button>
          </h2>
          <div id="irp2Section12" className="accordion-collapse collapse">
            <div className="accordion-body pt-3">
              <p className="text-muted small mb-3">
                Signatures confirm collaborative participation and informed consent. Family / caregiver signature is obtained with client consent.
              </p>
              <SignRow role="Client" sigField="client_signature" dateField="client_sign_date" roleText="Self" />
              <SignRow role="PRP (Lead Practitioner)" sigField="prp_signature" dateField="prp_sign_date" roleText="Lead Practitioner" />
              <SignRow role="Family Member / Caregiver" sigField="family_signature" dateField="family_sign_date" extraField="family_relationship" extraLabel="Relationship / Role" />
              <SignRow role="Other (specify)" sigField="other_signature" dateField="other_sign_date" extraField="other_specify" extraLabel="Specify" />

              <div className="row g-3 mt-2">
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="text" className="form-control" name="document_version" value={formData.document_version || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Document Version</label>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-floating form-floating-outline">
                    <input type="date" className="form-control" name="next_scheduled_review" value={formData.next_scheduled_review || ""} onChange={handleChange} disabled={readOnly} />
                    <label>Next Scheduled Review</label>
                  </div>
                </div>
              </div>
              <label className="form-label fw-semibold small mt-3">Stored in:</label>
              <div className="row g-2">
                <Check name="stored_electronic" label="Electronic Record" />
                <Check name="stored_paper" label="Paper File" />
                <Check name="stored_both" label="Both" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Irp2FormCtx.Provider>
  );
};

export default IndividualRehabPlanTwoForm;
